import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowDownZA,
  ArrowUpAZ,
  ArrowUpDown,
  Bookmark,
  CalendarClock,
  ChevronDown,
  Eye,
  Plus,
  Send,
  Star,
  X,
} from 'lucide-react';
import Loader from '../Loader';
import { CandidatePanel } from '../../sourcing/components/CandidateComponents';
import AddToSequenceModal from '../../sourcing/components/sequencing/AddToSequenceModal.jsx';
import PreviewModal from '../../sourcing/components/sequencing/PreviewModal.jsx';
import ScreeningBreakdownModal from '../../sourcing/components/screening/ScreeningBreakdownModal';
import {
  intentBadge,
  qualifiedBadge,
  channelBadge,
  StatusChip,
} from '../../sourcing/components/screening/responseBadges';
import useScheduleInterview from '../../sourcing/components/projects/useScheduleInterview';
import Pagination from './Pagination';
import { postAnalyticsEventService } from '../../Services/analyticsEvents.service';
import {
  patchFindCandidate,
  removeFindCandidate,
  removeFindCandidates,
  selectActiveCandidates,
  selectAllFindCandidates,
  selectCandidateFromFindList,
  sortFindCandidatesByCol,
  updatePage,
  updatePageSize,
} from '../../store/slices/findCandidatesSlice';

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

/* ── Pipeline status (mirrors STATUS_OPTIONS in CandidateComponents) ─────────── */
const STATUS_OPTIONS = [
  { value: 'not_contacted', label: 'Not Contacted', color: '#93c5fd' },
  { value: 'email_sent', label: 'Email Sent', color: '#0d9488' },
  { value: 'interested', label: 'Interested', color: '#a78bfa' },
  { value: 'rejected', label: 'Rejected', color: '#ef4444' },
  { value: 'not_interested', label: 'Not Interested', color: '#9ca3af' },
];

// Retired stages still exist on old rows. Falling back to STATUS_OPTIONS[0] would label
// them "Not Contacted" — plainly wrong for someone who was contacted — so an unrecognised
// value is shown as itself in neutral grey instead.
const humaniseStatus = (value) =>
  String(value)
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const statusMeta = (value) =>
  STATUS_OPTIONS.find((o) => o.value === value) ??
  (value ? { value, label: humaniseStatus(value), color: '#9ca3af' } : STATUS_OPTIONS[0]);

/**
 * Inline status control. The menu is fixed-positioned off the trigger's rect rather than
 * absolutely positioned, because the table body scrolls inside an overflow container that
 * would clip it on the last rows — the same reason the row menu was built this way.
 */
const STATUS_MENU_W = 176;
// Roughly 5 options × 36px + the menu's own padding. Only used to decide whether the menu
// opens downwards, so it needs to track STATUS_OPTIONS' length.
const STATUS_MENU_H = 196;

const StatusPill = ({ status, onChange }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const meta = statusMeta(status);

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const toggle = (e) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    const r = btnRef.current.getBoundingClientRect();
    const openDown = window.innerHeight - r.bottom > STATUS_MENU_H + 16;
    setPos({
      top: openDown ? r.bottom + 6 : r.top - STATUS_MENU_H - 6,
      left: Math.max(8, Math.min(r.left, window.innerWidth - STATUS_MENU_W - 8)),
    });
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-textSecondary transition-all hover:bg-gray-100 dark:hover:bg-gray-700/50"
      >
        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />
        <span className="whitespace-nowrap">{meta.label}</span>
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{ top: pos.top, left: pos.left, width: STATUS_MENU_W }}
            className="fixed z-[60] py-1 rounded-xl bg-bgPrimary border border-gray-200 dark:border-gray-700 shadow-xl"
          >
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onChange(opt.value);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors hover:bg-purple-50 dark:hover:bg-purple-500/10 ${
                  status === opt.value
                    ? 'bg-gray-50 dark:bg-gray-700/40 font-medium text-textPrimary dark:text-gray-100'
                    : 'text-textSecondary'
                }`}
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: opt.color }} />
                {opt.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

/** The brand gradient every other active/primary affordance in sourcing uses. */
const BRAND_GRADIENT = 'linear-gradient(to right, #ec4899, #3b82f6)';

/** SVG paint of the same gradient, for stroking/filling icon glyphs. Rendered once per
 *  table and referenced by id, since CSS gradients can't paint an SVG path. */
const ICON_GRADIENT_ID = 'fc-brand-icon-gradient';
const IconGradientDefs = () => (
  <svg width="0" height="0" className="absolute" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id={ICON_GRADIENT_ID} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ec4899" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
    </defs>
  </svg>
);

/** Floating bulk-action bar, centred over the bottom of the viewport while rows are selected. */
const SelectionBar = ({
  count,
  pageCount,
  allOnPageSelected,
  canSchedule,
  onSelectAllOnPage,
  onAddToSequence,
  onSchedule,
  onClear,
}) => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 pointer-events-none">
      <div
        className={`pointer-events-auto flex items-center gap-2 px-3 py-2.5 rounded-2xl bg-bgPrimary/95 backdrop-blur-md
          border border-purple-200/70 dark:border-purple-500/30
          shadow-[0_16px_44px_-10px_rgba(88,28,135,0.45)] transition-all duration-200 ease-out
          ${shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      >

        {!allOnPageSelected && pageCount > count && (
          <button
            onClick={onSelectAllOnPage}
            className="text-xs text-purple-600 dark:text-purple-300 hover:underline underline-offset-2 whitespace-nowrap"
          >
            Select all {pageCount}
          </button>
        )}

        {canSchedule && (
          <button
            onClick={onSchedule}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium whitespace-nowrap
              border border-gray-200 dark:border-gray-600 text-textSecondary
              hover:border-purple-400 hover:text-purple-600 dark:hover:text-purple-300 transition-all"
          >
            <CalendarClock className="w-4 h-4" />
            Schedule Interview ({count})
          </button>
        )}

        <button
          onClick={onAddToSequence}
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium text-white whitespace-nowrap hover:opacity-90 transition-opacity"
          style={{ background: BRAND_GRADIENT }}
        >
          <Send className="w-4 h-4" />
          Add to Outreach ({count})
        </button>

        <span className="mx-1 h-7 w-px bg-gray-200 dark:bg-gray-700" />

        <button
          onClick={onClear}
          title="Clear selection"
          className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Column sets per tab. Sourcing rows come from the sourced/saved/shortlist endpoints and
 * carry profile + pipeline fields; Outreach rows come from /sourcing/contacted and carry
 * engagement fields instead — so the header and cells swap together rather than showing
 * one superset with mostly-empty columns.
 */
// The three Sourcing sub-tabs share one column set — the same person should read the
// same way whichever list you found them in.
const SOURCING_COLUMNS = ['name', 'email', 'phone', 'sourced', 'role', 'status', 'actions'];

const COLUMNS = {
  SOURCING_ALL: SOURCING_COLUMNS,
  SOURCING_SAVED: SOURCING_COLUMNS,
  SOURCING_SHORTLISTED: SOURCING_COLUMNS,
  OUTREACH: ['name', 'email', 'sequence', 'initiated', 'responded', 'answers', 'outreach_status'],
};

const HEADINGS = {
  name: 'Candidate Name',
  email: 'Email ID',
  phone: 'Phone Number',
  sourced: 'Sourced At',
  role: 'Current Job Role',
  status: 'Status',
  actions: 'Actions',
  sequence: 'Outreach Name',
  initiated: 'Initiated On',
  responded: 'Responded At',
  answers: 'Response',
  outreach_status: 'Status',
};

/* ── Outreach progress (derived server-side in contacted_repo) ────────────────── */
const OUTREACH_STATUS_META = {
  pending: { label: 'Pending', color: '#93c5fd' },
  in_progress: { label: 'In-Progress', color: '#0d9488' },
  completed: { label: 'Completed', color: '#069855' },
  cancelled: { label: 'Cancelled', color: '#ef4444' },
  paused: { label: 'Paused', color: '#fbbf24' },
};

const columnsFor = (activeTab, activeSubTab) => {
  if (activeTab === 'OUTREACH') return COLUMNS.OUTREACH;
  return COLUMNS[`SOURCING_${activeSubTab}`] ?? COLUMNS.SOURCING_ALL;
};

/** Which slice field a column sorts on; columns absent here aren't sortable. */
const SORT_KEY = {
  name: 'full_name',
  role: 'current_title',
  sourced: 'sourced_at',
  responded: 'responded_at',
  sequence: 'sequence_name',
  initiated: 'initiated_at',
};

/** Matches formatDateOnly in InterviewManagementTable — plain YYYY-MM-DD, no time. */
const formatDateOnly = (value) => (value ? String(value).split('T')[0] : null);

/**
 * Email / phone cell. Sourced candidates come back without contact details — the list
 * endpoint doesn't surface them — so the cell offers a Reveal that hits the DB-only
 * lookup (`/sourcing/candidate/{id}/contact`, never CrustData, so it costs nothing).
 * Once revealed the value is written back onto the row so switching tabs keeps it.
 */
const ContactCell = ({ candidate, field, onRevealed }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const value =
    field === 'email'
      ? candidate.email
      : candidate.phone ||
        candidate.professional_phone ||
        candidate.personal_phone ||
        candidate.mobile;

  const reveal = async (e) => {
    e.stopPropagation();
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.get(
        `${SOURCING_URL}/sourcing/candidate/${candidate.candidate_id}/contact`,
      );
      if (data?.[field]) {
        onRevealed(candidate.candidate_id, {
          [field]: data[field],
          ...(field === 'email' ? { email_type: data.email_type } : {}),
        });
      } else {
        setError(field === 'email' ? 'No email on file' : 'No phone on file');
      }
    } catch {
      setError('Try again');
    } finally {
      setBusy(false);
    }
  };

  if (value) {
    return field === 'email' ? (
      <a
        href={`mailto:${value}`}
        onClick={(e) => e.stopPropagation()}
        className="text-purple-600 hover:underline break-all"
      >
        {value}
      </a>
    ) : (
      <span className="whitespace-nowrap">{value}</span>
    );
  }

  // A lookup that came back empty is a fact about the candidate, not a failed action —
  // say so instead of leaving a button that will never succeed.
  if (error) return <span className="text-xs text-gray-400">{error}</span>;

  return (
    <button
      onClick={reveal}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-gray-300 dark:border-gray-600 text-textSecondary hover:border-purple-400 hover:text-purple-600 disabled:opacity-60 transition-all"
    >
      <Eye className="w-3.5 h-3.5" />
      {busy ? 'Revealing…' : 'Reveal'}
    </button>
  );
};

function FindCandidatesTable({ portal = 'org-admin', onRefetch }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const activeTab = useSelector((state) => state.findCandidatesSlice.activeTab);
  const activeSubTab = useSelector((state) => state.findCandidatesSlice.activeSubTab);
  const selectedJobId = useSelector((state) => state.findCandidatesSlice.selectedJobId);
  // Passed to the enroll flow, where it's what {{job_role}} resolves to.
  const selectedJobRole = useSelector((state) => state.findCandidatesSlice.selectedJobRole);
  const candidates = useSelector(selectActiveCandidates);
  const totalCount = useSelector((state) => state.findCandidatesSlice.totalCount);
  const loading = useSelector((state) => state.findCandidatesSlice.loading);
  const jobsLoading = useSelector((state) => state.findCandidatesSlice.jobsLoading);
  const error = useSelector((state) => state.findCandidatesSlice.error);
  const currentPage = useSelector((state) => state.findCandidatesSlice.currentPage);
  const pageSize = useSelector((state) => state.findCandidatesSlice.pageSize);
  const selectionCount = useSelector((state) => state.findCandidatesSlice.selectionCount);
  const selectedCandidates = useSelector((state) => state.findCandidatesSlice.selectedCandidates);
  const sortConfig = useSelector((state) => state.findCandidatesSlice.sortConfig);

  const recruiterName =
    useSelector((state) => state.userSlice?.name) || localStorage.getItem('username') || '';
  const orgId = localStorage.getItem('current_org_id');
  const recruiterId = localStorage.getItem('org_admin_id');

  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [addToSeqCandidates, setAddToSeqCandidates] = useState(null);
  const [previewState, setPreviewState] = useState(null);
  const [enrollResult, setEnrollResult] = useState(null);
  const [breakdownRow, setBreakdownRow] = useState(null);

  // Scheduling needs a concrete job's interview config — only offered on a single job.
  // Scheduled candidates belong to Interview Management, so they leave every sourcing
  // sub-tab; the refetch then backfills this page from the server.
  const handleScheduled = (candidateIds) => {
    dispatch(removeFindCandidates(candidateIds));
    setSelectedCandidate(null);
    onRefetch?.();
  };
  const { startBulkSchedule, scheduleUI, canSchedule } = useScheduleInterview(
    selectedJobId,
    handleScheduled,
  );

  const columns = columnsFor(activeTab, activeSubTab);
  const isOutreach = activeTab === 'OUTREACH';
  const isShortlistTab = !isOutreach && activeSubTab === 'SHORTLISTED';
  const isSavedTab = !isOutreach && activeSubTab === 'SAVED';

  const allOnPageSelected = candidates.length > 0 && candidates.every((c) => c.isSelected);
  const someOnPageSelected = !allOnPageSelected && candidates.some((c) => c.isSelected);

  // The panel renders from the live list row, not the object captured when the row was
  // clicked. Shortlisting (and save/status changes) patch the row in the store, so a held
  // copy would keep showing the state as it was when the panel opened — the row behind it
  // updating while the panel's own button didn't. Falls back to the captured object for a
  // candidate the list has since dropped, so the panel doesn't blank out mid-action.
  const panelCandidate = selectedCandidate
    ? candidates.find((c) => c.candidate_id === selectedCandidate.candidate_id) ?? selectedCandidate
    : null;

  // asc → desc → unsorted, the same cycle the interview table uses.
  const handleSort = (col) => {
    const key = SORT_KEY[col];
    if (!key) return;
    const direction =
      sortConfig.key !== key ? 'asc' : sortConfig.direction === 'asc' ? 'desc' : null;
    dispatch(sortFindCandidatesByCol({ key: direction ? key : null, direction }));
  };

  const SortIcon = ({ col }) => {
    const key = SORT_KEY[col];
    if (!key) return null;
    const cls = 'w-3.5 h-3.5 ml-1 shrink-0';
    if (sortConfig.key !== key) return <ArrowUpDown className={`${cls} opacity-40`} />;
    if (sortConfig.direction === 'asc') return <ArrowUpAZ className={cls} />;
    if (sortConfig.direction === 'desc') return <ArrowDownZA className={cls} />;
    return <ArrowUpDown className={`${cls} opacity-40`} />;
  };

  /* ── row actions ───────────────────────────────────────────────────────────── */

  const handleSaveToggle = async (candidate) => {
    const isSaved = candidate.is_saved ?? activeSubTab === 'SAVED';
    try {
      if (isSaved) {
        await axios.delete(`${SOURCING_URL}/sourcing/saved/${candidate.candidate_id}`, {
          params: { recruiter_id: recruiterId },
        });
        // On the Saved tab the row no longer belongs in the list; elsewhere it just flips.
        if (activeSubTab === 'SAVED') {
          dispatch(removeFindCandidate(candidate.candidate_id));
          onRefetch?.(); // backfill this page from the server
        } else {
          dispatch(patchFindCandidate({ candidateId: candidate.candidate_id, changes: { is_saved: false } }));
        }
      } else {
        await axios.post(`${SOURCING_URL}/sourcing/saved`, {
          candidate_id: candidate.candidate_id,
          recruiter_id: recruiterId,
          organisation_id: orgId,
        });
        dispatch(patchFindCandidate({ candidateId: candidate.candidate_id, changes: { is_saved: true } }));
      }
    } catch {
      /* leave the row as-is; the next fetch reconciles */
    }
  };

  const handleShortlistToggle = async (candidate) => {
    const isShortlisted = candidate.is_shortlisted ?? activeSubTab === 'SHORTLISTED';
    // Shortlists are per-search. The list endpoints hand back the search to file under —
    // the shortlist row's own search when there is one, otherwise the search the candidate
    // was sourced from.
    const searchId = candidate.search_id || null;
    try {
      if (isShortlisted) {
        await axios.delete(`${SOURCING_URL}/sourcing/shortlist/${candidate.candidate_id}`, {
          params: {
            organisation_id: orgId,
            recruiter_id: recruiterId,
            recruiter_name: recruiterName,
            search_id: searchId,
          },
        });
        if (activeSubTab === 'SHORTLISTED') {
          dispatch(removeFindCandidate(candidate.candidate_id));
          onRefetch?.();
        } else {
          dispatch(
            patchFindCandidate({ candidateId: candidate.candidate_id, changes: { is_shortlisted: false } }),
          );
        }
      } else {
        const { data: added } = await axios.post(`${SOURCING_URL}/sourcing/shortlist`, {
          candidate_id: candidate.candidate_id,
          organisation_id: orgId,
          recruiter_id: recruiterId,
          recruiter_name: recruiterName,
          search_id: searchId,
        });
        // The Sourcing API answers with the job the search belongs to and
        // its shortlist size; an already-shortlisted candidate is not counted
        if (!added?.already) {
          postAnalyticsEventService('shortlist_saved', {
            search_id: searchId || undefined,
            role_id: added?.job_id || undefined,
            shortlist_size: added?.shortlist_size ?? undefined,
          });
        }
        // Shortlisting auto-unsaves server-side, so the Saved tab loses the row.
        if (activeSubTab === 'SAVED') {
          dispatch(removeFindCandidate(candidate.candidate_id));
          onRefetch?.();
        } else {
          dispatch(
            patchFindCandidate({
              candidateId: candidate.candidate_id,
              changes: { is_shortlisted: true, is_saved: false },
            }),
          );
        }
      }
    } catch (err) {
      if (err.response?.status === 409) {
        dispatch(
          patchFindCandidate({ candidateId: candidate.candidate_id, changes: { is_shortlisted: true } }),
        );
      }
    }
  };

  // Pipeline status is a standalone outreach stage — independent of saved/shortlisted.
  // It is per-role; the job-scope guard should never trip now that a job is always
  // selected, but it stops an unscoped row ever being written if that changes.
  const handleStatusChange = (candidate, newStatus) => {
    if (newStatus === candidate.status || !selectedJobId) return;
    dispatch(
      patchFindCandidate({ candidateId: candidate.candidate_id, changes: { status: newStatus } }),
    );
    axios
      .post(`${SOURCING_URL}/sourcing/pipeline`, {
        candidate_id: candidate.candidate_id,
        recruiter_id: recruiterId,
        organisation_id: orgId,
        action: newStatus,
        // Status belongs to a role, and the screen always has one in scope.
        job_id: selectedJobId,
      })
      .catch(() => {});
    axios
      .post(`${SOURCING_URL}/sourcing/activity`, {
        action: 'status_changed',
        candidate_id: candidate.candidate_id,
        recruiter_id: recruiterId,
        organisation_id: orgId,
        metadata: {
          recruiter_name: recruiterName,
          candidate_name: candidate.full_name,
          new_status: newStatus,
          new_status_label: statusMeta(newStatus).label,
          // The activity log is org-wide; stamping the job lets the profile show which
          // role each entry was about.
          job_id: selectedJobId,
        },
      })
      .catch(() => {});
  };

  // Write a revealed email/phone back onto the row so it survives paging and tab
  // switches without another lookup.
  const handleRevealed = (candidateId, changes) =>
    dispatch(patchFindCandidate({ candidateId, changes }));

  const handleFindSimilar = (candidate) => {
    const yearsExp = candidate.years_of_experience;
    const loc =
      candidate.location_city || candidate.location_state || candidate.location_country || null;
    const skills = candidate.skills || [];

    setSelectedCandidate(null);
    navigate(`/${portal}/sourcing/results`, {
      state: {
        query: `Similar profiles to ${candidate.full_name}${
          candidate.current_title ? ` – ${candidate.current_title}` : ''
        }`,
        canonical_query: {
          roles: [candidate.current_title].filter(Boolean),
          skills_required: skills.slice(0, 5),
          skills_nice_to_have: skills.slice(5, 10),
          locations: loc ? [loc] : [],
          seniority: candidate.seniority || null,
          experience_min: yearsExp != null ? Math.max(0, yearsExp - 3) : null,
          experience_max: yearsExp != null ? yearsExp + 3 : null,
          companies: [],
          limit: 10,
        },
        source_candidate: { name: candidate.full_name, title: candidate.current_title },
      },
    });
  };

  // Selection lives on the rows themselves (isSelected), the same as the interview lists.
  const selectedCandidateRows = selectedCandidates;

  /* ── cells ─────────────────────────────────────────────────────────────────── */

  // Lifted verbatim from CommonTable (the Screened Candidates table) so the two read as
  // one component — same header weight, same row type scale, same cell padding.
  const th = 'text-left px-4 py-4 text-sm font-medium dark:text-[#CCCCCC] text-textSecondary';
  const td = 'p-4 text-sm dark:text-[#CCCCCC] text-textSecondary';

  const renderCell = (col, c) => {
    switch (col) {
      case 'name':
        return (
          <td key={col} className={`${td} font-medium dark:font-medium max-w-[260px]`}>
            <div className="flex items-center gap-3 min-w-0">
              {/* <CandidateAvatar candidate={c} size={32} /> */}
              <div className="min-w-0">
                {/* Name only — the email has its own column in every tab's column set,
                    so repeating it under the name was duplicate information. */}
                <span className="truncate block">{c.full_name || 'Unknown'}</span>
              </div>
            </div>
          </td>
        );
      case 'email':
        return (
          <td key={col} className={`${td} max-w-[260px]`} onClick={(e) => e.stopPropagation()}>
            <ContactCell candidate={c} field="email" onRevealed={handleRevealed} />
          </td>
        );
      case 'phone':
        return (
          <td key={col} className={td} onClick={(e) => e.stopPropagation()}>
            <ContactCell candidate={c} field="phone" onRevealed={handleRevealed} />
          </td>
        );
      case 'sourced':
        return (
          <td key={col} className={`${td} whitespace-nowrap tabular-nums`}>
            {formatDateOnly(c.sourced_at) || '—'}
          </td>
        );
      case 'role':
        return (
          <td key={col} className={`${td} max-w-[220px] truncate`} title={c.current_title || ''}>
            {c.current_title || '—'}
          </td>
        );
      case 'status':
        return (
          <td key={col} className={td} onClick={(e) => e.stopPropagation()}>
            <StatusPill status={c.status} onChange={(s) => handleStatusChange(c, s)} />
          </td>
        );
      case 'sequence':
        return (
          <td key={col} className={td}>
            {c.sequence_name || <span className="text-gray-300">—</span>}
          </td>
        );
      case 'qualified': {
        const badge = qualifiedBadge(c.is_qualified);
        return (
          <td key={col} className={td}>
            {badge ? <StatusChip badge={badge} /> : <span className="text-gray-300">—</span>}
          </td>
        );
      }
      case 'intent':
        return (
          <td key={col} className={td}>
            <div className="flex items-center gap-1.5">
              <StatusChip badge={intentBadge(c.intent)} />
              <StatusChip badge={channelBadge(c.channel)} />
            </div>
          </td>
        );
      case 'initiated':
        return (
          <td key={col} className={`${td} tabular-nums whitespace-nowrap`}>
            {c.initiated_at ? (
              new Date(c.initiated_at).toLocaleDateString()
            ) : (
              <span className="text-gray-300">—</span>
            )}
          </td>
        );
      case 'responded':
        return (
          <td key={col} className={`${td} tabular-nums whitespace-nowrap`}>
            {c.responded_at ? (
              new Date(c.responded_at).toLocaleDateString()
            ) : (
              <span className="text-gray-300">—</span>
            )}
          </td>
        );
      case 'outreach_status': {
        const meta = OUTREACH_STATUS_META[c.outreach_status];
        return (
          <td key={col} className={td}>
            {meta ? (
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: meta.color }}
                />
                {meta.label}
              </span>
            ) : (
              <span className="text-gray-300">—</span>
            )}
          </td>
        );
      }
      case 'answers': {
        const hasAnswers =
          !!c.engagement_status && (c.responses || (c.score_breakdown || []).length > 0);
        return (
          <td key={col} className={td} onClick={(e) => e.stopPropagation()}>
            {hasAnswers ? (
              <button
                onClick={() => setBreakdownRow(c)}
                className="text-xs font-medium text-purple-600 hover:text-purple-700"
              >
                View
              </button>
            ) : (
              <span className="text-gray-300">—</span>
            )}
          </td>
        );
      }
      case 'actions': {
        const isSaved = c.is_saved ?? activeSubTab === 'SAVED';
        const isShortlisted = c.is_shortlisted ?? activeSubTab === 'SHORTLISTED';
        return (
          <td key={col} className={td} onClick={(e) => e.stopPropagation()}>
            {/* Star vs Bookmark: the previous BookmarkCheck/Bookmark pair differed only by a
                tick and read as the same icon at 16px. Active fills the glyph itself with the
                brand gradient (see IconGradientDefs) rather than tinting it a flat colour. */}
            <div className="flex items-center justify-start gap-1.5">
              <button
                onClick={() => handleShortlistToggle(c)}
                title={isShortlisted ? 'Remove from shortlist' : 'Shortlist'}
                aria-label={isShortlisted ? 'Remove from shortlist' : 'Shortlist'}
                className="p-1.5 rounded-full text-gray-400 hover:bg-pink-50 dark:hover:bg-pink-500/10 transition-all"
              >
                <Star
                  className="w-4 h-4"
                  fill={isShortlisted ? `url(#${ICON_GRADIENT_ID})` : 'none'}
                  stroke={isShortlisted ? `url(#${ICON_GRADIENT_ID})` : 'currentColor'}
                />
              </button>
              <button
                onClick={() => handleSaveToggle(c)}
                title={isSaved ? 'Remove from saved' : 'Save for later'}
                aria-label={isSaved ? 'Remove from saved' : 'Save for later'}
                className="p-1.5 rounded-full text-gray-400 hover:bg-pink-50 dark:hover:bg-pink-500/10 transition-all"
              >
                <Bookmark
                  className="w-4 h-4"
                  fill={isSaved ? `url(#${ICON_GRADIENT_ID})` : 'none'}
                  stroke={isSaved ? `url(#${ICON_GRADIENT_ID})` : 'currentColor'}
                />
              </button>
            </div>
          </td>
        );
      }
      default:
        return <td key={col} className={td} />;
    }
  };

  /* ── render ────────────────────────────────────────────────────────────────── */

  // The first list fetch waits on the job roles resolving, so treat that window as
  // loading too — otherwise every page load flashes "No candidates found" first.
  if (loading || (jobsLoading && !selectedJobId)) {
    return (
      <div className="w-full h-[70vh] flex flex-row items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-24 w-full">
        <p className="text-sm text-red-400">{error}</p>
        <button
          onClick={() => onRefetch?.()}
          className="mt-4 px-4 py-2 rounded-full text-sm text-purple-600 border border-purple-200 hover:bg-purple-50"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="w-full h-full mt-3 pl-2 flex flex-col overflow-hidden">
      <IconGradientDefs />
      {selectionCount > 0 && (
        <SelectionBar
          count={selectionCount}
          pageCount={candidates.length}
          allOnPageSelected={allOnPageSelected}
          canSchedule={canSchedule}
          onSelectAllOnPage={() => dispatch(selectAllFindCandidates(true))}
          onAddToSequence={() => setAddToSeqCandidates(selectedCandidateRows)}
          onSchedule={() => startBulkSchedule(selectedCandidateRows)}
          onClear={() => dispatch(selectAllFindCandidates(false))}
        />
      )}

      <div className="flex-1 min-h-[70vh] max-h-full rounded-lg border-gray-700/50 overflow-y-auto">
        <table className="w-full relative">
          <thead className=" z-10 bg-bgSecondary">
            <tr className="border-b border-[#5A4479D4]">
              <th className={`${th} w-12 py-4`}>
                {candidates.length > 0 && (
                  <input
                    type="checkbox"
                    className="rounded bg-gray-700 border-gray-600 w-4 h-4 accent-blue-600"
                    checked={allOnPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someOnPageSelected;
                    }}
                    onChange={(e) => dispatch(selectAllFindCandidates(e.target.checked))}
                    title="Select All"
                  />
                )}
              </th>
              {columns.map((col) => (
                <th
                  key={col}
                  className={`${th} ${SORT_KEY[col] ? 'cursor-pointer' : ''}`}
                  onClick={() => handleSort(col)}
                >
                  <div className="flex items-center">
                    {HEADINGS[col]}
                    <SortIcon col={col} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {candidates.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="h-72 text-center">
                  <div className="flex h-full flex-col items-center justify-center">
                    <h2 className="text-lg font-medium text-gray-400 mb-1">
                      No candidates found
                    </h2>
                    <p className="text-sm text-gray-400">
                      {isOutreach
                        ? 'Enroll candidates in a sequence to start seeing outreach here.'
                        : isShortlistTab
                          ? 'Shortlist candidates from your search results to see them here.'
                          : isSavedTab
                            ? 'Save candidates from your search results to see them here.'
                            : 'Run a search against a job role to start building this list.'}
                    </p>
                    {/* The empty All list is a dead end otherwise — the only way out is
                        the search screen, so offer it here as well as in the header. Carries
                        the job in scope, exactly like the header's New Search. Saved and
                        Shortlisted fill from actions on existing results, not a new search. */}
                    {!isOutreach && !isSavedTab && !isShortlistTab && (
                      <button
                        onClick={() =>
                          navigate(`/${portal}/sourcing`, {
                            state: selectedJobId
                              ? { job_id: selectedJobId, job_role: selectedJobRole }
                              : undefined,
                          })
                        }
                        className="border-btn border-btn-dark mt-5 flex items-center gap-1.5 rounded-full text-sm font-medium transition-all"
                      >
                        Start Searching
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}

            {candidates.map((c) => (
              <tr
                key={c.candidate_id}
                onClick={() => setSelectedCandidate(c)}
                className="border-b dark:border-gray-700/50 border-gray-300/50 dark:hover:bg-gray-800 hover:bg-gray-100 transition-colors cursor-pointer group"
              >
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="rounded bg-gray-700 border-gray-600 w-4 h-4 accent-blue-600"
                    checked={!!c.isSelected}
                    onChange={() => dispatch(selectCandidateFromFindList(c))}
                  />
                </td>
                {columns.map((col) => renderCell(col, c))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <div className="pt-4">
          <Pagination
            currentPage={currentPage}
            totalItems={totalCount}
            itemsPerPage={pageSize}
            onPageChange={(p) => dispatch(updatePage(p))}
            onItemsPerPageChange={(size) => dispatch(updatePageSize(size))}
            label="Candidates"
          />
        </div>
      )}

      {panelCandidate && (
        <CandidatePanel
          candidate={panelCandidate}
          onClose={() => setSelectedCandidate(null)}
          shortlisted={panelCandidate.is_shortlisted ?? activeSubTab === 'SHORTLISTED'}
          onShortlist={handleShortlistToggle}
          onContactUpdate={handleRevealed}
          onEnroll={(c) => setAddToSeqCandidates([c])}
          onFindSimilar={handleFindSimilar}
          jobId={selectedJobId}
          onScheduled={handleScheduled}
        />
      )}

      {addToSeqCandidates && (
        <AddToSequenceModal
          candidates={addToSeqCandidates}
          jobId={selectedJobId}
          jobRole={selectedJobRole}
          onClose={() => setAddToSeqCandidates(null)}
          onPreview={(seq, list) => {
            setPreviewState({ candidates: list, sequence: seq });
            setAddToSeqCandidates(null);
          }}
          onEnrolled={(summary) => {
            setAddToSeqCandidates(null);
            dispatch(selectAllFindCandidates(false));
            setEnrollResult(summary);
            setTimeout(() => setEnrollResult(null), 6000);
          }}
        />
      )}

      {previewState && (
        <PreviewModal
          candidates={previewState.candidates}
          sequence={previewState.sequence}
          jobId={selectedJobId}
          jobRole={selectedJobRole}
          onClose={() => setPreviewState(null)}
          onBack={() => {
            setAddToSeqCandidates(previewState.candidates);
            setPreviewState(null);
          }}
          onEnroll={(summary) => {
            setPreviewState(null);
            dispatch(selectAllFindCandidates(false));
            setEnrollResult(summary);
            setTimeout(() => setEnrollResult(null), 6000);
          }}
        />
      )}

      {breakdownRow && (
        <ScreeningBreakdownModal
          candidate={breakdownRow}
          responses={breakdownRow.responses}
          scoreBreakdown={breakdownRow.score_breakdown}
          isQualified={breakdownRow.is_qualified}
          intent={breakdownRow.intent}
          submittedAt={breakdownRow.responded_at}
          formId={breakdownRow.form_id}
          formQuestions={breakdownRow.questions_snapshot}
          onClose={() => setBreakdownRow(null)}
        />
      )}

      {enrollResult && (
        <div
          className={`fixed top-5 right-5 z-[100] flex items-start gap-2 px-4 py-3 bg-white rounded-xl shadow-lg border text-sm max-w-sm ${
            enrollResult.tone === 'error'
              ? 'border-red-200 text-red-700'
              : enrollResult.tone === 'warn'
                ? 'border-amber-200 text-amber-700'
                : 'border-green-200 text-green-700'
          }`}
        >
          {enrollResult.message}
        </div>
      )}

      {scheduleUI}
    </div>
  );
}

export default FindCandidatesTable;
